import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  Skeleton,
} from "@mui/material";
import {
  LocalHospital,
  People,
  ExitToApp,
  Bed,
  Assignment,
  Refresh,
  AccessTime,
  Person,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../context";
import { getICUDashboard } from "../../../api/icu";

// StatCard component similar to IPD
const StatCard = ({
  icon,
  title,
  value,
  amount,
  onClick,
  theme,
  loading = false,
  highlight,
  cardSx,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card sx={{ height: "100%", p: 2, borderRadius: 3, boxShadow: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={180} />
      </Card>
    );
  }

  const capitalizeTitle = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const isSummaryCard = !!cardSx;

  const renderAmount = (amt, idx) => {
    return (
      <Typography
        key={idx}
        variant={idx === 0 ? "h4" : "body1"}
        fontWeight="bold"
        sx={{
          color: cardSx?.color || theme.palette.primary.contrastText,
          lineHeight: 1.2,
          mt: idx > 0 ? 1 : 0,
          fontSize: cardSx ? 32 : undefined,
          textShadow: cardSx ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
        }}
      >
        {amt}
      </Typography>
    );
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "default",
        transition: "all 0.3s ease",
        border: 0,
        borderRadius: 4,
        boxShadow: isSummaryCard ? "0 8px 32px 0 rgba(31, 38, 135, 0.15)" : 2,
        p: isSummaryCard ? 4 : 2,
        background: isSummaryCard
          ? `linear-gradient(135deg, ${cardSx?.bgcolor} 60%, ${theme.palette.background.paper} 100%)`
          : cardSx?.bgcolor || theme.palette.primary.main,
        color: cardSx?.color || theme.palette.primary.contrastText,
        position: "relative",
        borderTop: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        borderBottom: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        ...cardSx,
        "&:hover": {
          boxShadow: 2,
        },
      }}
      aria-label={`${title} card`}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: isSummaryCard ? 2 : 1,
          }}
        >
          {icon && (
            <Box
              sx={{
                p: isSummaryCard ? 1.5 : 1,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
        <Typography
          variant={isSummaryCard ? "h5" : "body2"}
          fontWeight="bold"
          sx={{
            color: cardSx?.color || theme.palette.primary.contrastText,
            mb: 1,
            textAlign: "center",
          }}
        >
          {capitalizeTitle(title)}
        </Typography>
        {value && (
          <Box sx={{ textAlign: "center", width: "100%" }}>
            {Array.isArray(value) ? (
              value.map((amt, idx) => renderAmount(amt, idx))
            ) : (
              <Typography
                variant={isSummaryCard ? "h4" : "h5"}
                fontWeight="bold"
                sx={{
                  color: cardSx?.color || theme.palette.primary.contrastText,
                  fontSize: cardSx ? 32 : undefined,
                  textShadow: cardSx ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
        )}
        {amount && (
          <Typography
            variant="body2"
            sx={{
              color: cardSx?.color || theme.palette.primary.contrastText,
              opacity: 0.9,
              mt: 1,
            }}
          >
            {amount}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

const ICUDashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const theme = useTheme();

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["icu-dashboard"],
    queryFn: () => getICUDashboard(token),
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t(
            "icu.dashboard.error_loading",
            "Unable to load dashboard data. Showing empty dashboard. Please check your connection and try refreshing."
          )}
          {error && (
            <Box sx={{ mt: 1, fontSize: "0.875rem" }}>
              {t("common.error", "Error")}:{" "}
              {error.message || t("common.unknown_error", "Unknown error")}
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  const data = dashboardData || {
    total_admissions: 0,
    current_admissions: 0,
    discharged_today: 0,
    transferred_today: 0,
    total_beds: 0,
    available_beds: 0,
    occupied_beds: 0,
    cleaning_beds: 0,
    maintenance_beds: 0,
    nurses_on_duty: 0,
    rounds_pending: 0,
    rounds_completed_today: 0,
    total_revenue: 0,
    pending_payments: 0,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          {t("icu.dashboard.title", "ICU Dashboard")}
        </Typography>
        <Tooltip title={t("common.refresh", "Refresh")}>
          <IconButton onClick={() => refetch()} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            icon={<LocalHospital sx={{ fontSize: 40 }} />}
            title={t("icu.dashboard.total_admissions", "Total Admissions")}
            value={data.total_admissions || 0}
            theme={theme}
            cardSx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderLeft: theme.palette.primary.main,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<People sx={{ fontSize: 32 }} />}
            title={t("icu.dashboard.current_admissions", "Current")}
            value={data.current_admissions || 0}
            theme={theme}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<ExitToApp sx={{ fontSize: 32 }} />}
            title={t("icu.dashboard.discharged_today", "Discharged")}
            value={data.discharged_today || 0}
            theme={theme}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<Assignment sx={{ fontSize: 32 }} />}
            title={t("icu.dashboard.transferred_today", "Transferred")}
            value={data.transferred_today || 0}
            theme={theme}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            icon={<Bed sx={{ fontSize: 32 }} />}
            title={t("icu.dashboard.occupied_beds", "Occupied")}
            value={`${data.occupied_beds || 0}/${data.total_beds || 0}`}
            theme={theme}
          />
        </Grid>
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Bed color="success" />
                <Typography variant="h6">
                  {t("icu.dashboard.available_beds", "Available Beds")}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {data.available_beds || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Person color="primary" />
                <Typography variant="h6">
                  {t("icu.dashboard.nurses_on_duty", "Nurses on Duty")}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {data.nurses_on_duty || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccessTime color="warning" />
                <Typography variant="h6">
                  {t("icu.dashboard.rounds_pending", "Rounds Pending")}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {data.rounds_pending || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Assignment color="success" />
                <Typography variant="h6">
                  {t("icu.dashboard.rounds_completed", "Completed Today")}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {data.rounds_completed_today || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ICUDashboard;

